import { useCallback, useEffect, useMemo, useRef } from 'react';

import { extend } from '@react-three/fiber';
import { MeshLineGeometry, MeshLineMaterial } from 'meshline';
import * as THREE from 'three';

export type TrackPoint = [number, number, number] | null;
export type TrackData = TrackPoint[];

declare module '@react-three/fiber' {
    interface ThreeElements {
        meshLineGeometry: Omit<ThreeElements['bufferGeometry'], 'args' | 'points'> & {
            points: THREE.Vector3[];
        };
        meshLineMaterial: Omit<ThreeElements['material'], 'args'> & {
            lineWidth?: number;
            color?: THREE.ColorRepresentation;
            opacity?: number;
            transparent?: boolean;
        };
    }
}

extend({
    MeshLineGeometry,
    MeshLineMaterial,
});

// Vector Pooling
const POOL_SIZE = 256;
const vectorPool: THREE.Vector3[] = Array.from({ length: POOL_SIZE }, () => new THREE.Vector3());
const availableVectors = [...vectorPool];

function getVector(x = 0, y = 0, z = 0): THREE.Vector3 {
    if (availableVectors.length === 0) {
        return new THREE.Vector3(x, y, z);
    }
    const vector = availableVectors.pop()!;
    vector.set(x, y, z);
    return vector;
}

function returnVectors(vectors: THREE.Vector3[]): void {
    for (const vector of vectors) {
        if (availableVectors.length < POOL_SIZE) {
            availableVectors.push(vector);
        }
    }
}

export type SplitPointConfigItem = {
    min: number; // Min frame/track index for this segment
    max: number; // Max frame/track index for this segment
    color: THREE.ColorRepresentation;
    lineWidth?: number;
};

export type MultiColorTracerProps = {
    frame?: number;
    track: TrackData;
    splitPointConfig: SplitPointConfigItem[];
    defaultLineWidth?: number;
    opacity?: number;
};

export function MultiColorTracer({
    frame = 0,
    track,
    splitPointConfig,
    defaultLineWidth = 0.01,
    opacity = 1,
}: MultiColorTracerProps) {
    const checkedOutVectors = useRef<THREE.Vector3[]>([]);

    const stableCheckout = useCallback((x = 0, y = 0, z = 0) => {
        const v = getVector(x, y, z);
        checkedOutVectors.current.push(v);
        return v;
    }, []);

    const stableResetVectors = useCallback(() => {
        returnVectors(checkedOutVectors.current);
        checkedOutVectors.current = [];
    }, []);

    // Makes individual meshes according to the provided split config
    const lineSegments = useMemo(() => {
        stableResetVectors(); // Free up all previously used vectors
        const segments = [];
        const currentMaxTrackIndexProcessedByFrame = Math.min(frame, track.length - 1); // Determine the furthest point in the track to render based on current frame

        for (let segmentIdx = 0; segmentIdx < splitPointConfig.length; segmentIdx++) {
            const config = splitPointConfig[segmentIdx];
            const pointsForThisSegment: THREE.Vector3[] = [];

            const configuredMinTrackIndex = config.min; // Minimum track index defined for this segment.
            const configuredMaxTrackIndex = config.max; // Maximum track index defined for this segment.

            const actualStartTrackIndexForNewPoints = configuredMinTrackIndex;
            const actualEndTrackIndexForSegment = Math.min(
                configuredMaxTrackIndex,
                currentMaxTrackIndexProcessedByFrame,
            );

            // Check if the current segment is active and should be processed based on the frame.
            if (
                actualStartTrackIndexForNewPoints <= actualEndTrackIndexForSegment &&
                actualStartTrackIndexForNewPoints <= currentMaxTrackIndexProcessedByFrame
            ) {
                // Gap-fixing: Connect to the previous segment to ensure the final result is a continous line
                if (segmentIdx > 0) {
                    const prevConfig = splitPointConfig[segmentIdx - 1]; // Config of the immediately preceding segment
                    // Check if this segment should directly follow the previous one and if the connection point is visible
                    if (
                        prevConfig.max === configuredMinTrackIndex - 1 && // Segments are contiguous
                        prevConfig.max <= currentMaxTrackIndexProcessedByFrame && // Connection point is within the currently rendered part of the track
                        prevConfig.max < track.length &&
                        prevConfig.max >= 0 // In bounds
                    ) {
                        const connectingPointData = track[prevConfig.max]; // Get the coordinate data of the previous segment's last point
                        if (connectingPointData) {
                            pointsForThisSegment.push(stableCheckout(...connectingPointData)); // Add as the first point of the current segment
                        }
                    }
                }

                // Add points for the current segment's own range
                for (let i = actualStartTrackIndexForNewPoints; i <= actualEndTrackIndexForSegment; i++) {
                    // Iterate from this segment's start to its calculated end for the current frame
                    if (i < 0 || i >= track.length) continue; // Out of track bounds
                    const pData = track[i];
                    if (pData) {
                        pointsForThisSegment.push(stableCheckout(...pData));
                    }
                }
            }

            segments.push({
                points:
                    pointsForThisSegment.length < 2 // A line needs at least 2 points.
                        ? []
                        : pointsForThisSegment,
                color: config.color,
                lineWidth: config.lineWidth ?? defaultLineWidth,
                key: `segment-${config.min}-${config.max}-${config.color}-${config.lineWidth ?? defaultLineWidth}`,
            });
        }
        return segments;
    }, [frame, track, stableCheckout, stableResetVectors, splitPointConfig, defaultLineWidth]);

    useEffect(() => {
        return () => {
            stableResetVectors(); // Return all checked-out vectors to the pool
        };
    }, [stableResetVectors]);

    return (
        <group>
            {lineSegments.map((segment) =>
                segment.points.length >= 2 ? (
                    <mesh key={segment.key}>
                        <meshLineGeometry points={segment.points} />
                        <meshLineMaterial
                            transparent
                            lineWidth={segment.lineWidth}
                            color={segment.color}
                            opacity={opacity}
                        />
                    </mesh>
                ) : null,
            )}
        </group>
    );
}
